import { useState } from "react";
import { titelFoto, titelRundum } from "../dienste/fotos.js";
import { bewegungenVonWein, gesamtFlaschen, jahrgangFuer, jahrgangTitel, kritikenFuer, rebsortenText, regionZuWein, trinkfensterText, winzerZuWein } from "../kern/fachlogik.js";
import { fmtEuro, fmtZahl, jahrLabel, normiert, wertFormat } from "../kern/format.js";
import { FLASCHENGROESSEN, SCHEMATA } from "../kern/konstanten.js";
import { BestandAnzeige, Btn, FotoBild, KritikBalken, Leer, LoeschenKnopf, Seitenkopf, TextKnopf, TypPunkt, ZeilenMenue, jahrgangsMenue } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";
import { Begrenzt } from "../ui/begrenzt.jsx";
import { RundumVorschau } from "../ui/flasche.jsx";
import { LISTEN_GRENZEN as G } from "../kern/konstanten.js";


function WeinDetail({ id, db, fremdeKritiken = null, nurBestand = false, geheZu, oeffneModal, loescheWein, loescheJahrgang, setzeFavorit }) {
  /* Hooks stehen vor jedem bedingten Return — sonst ändert sich ihre
     Zahl zwischen zwei Renderdurchläufen (Regeln für Hooks). */
  const [fotoGross, setFotoGross] = useState(false);
  const [zeigeFremde, setZeigeFremde] = useState(false);
  const wein = db.weine.find((w) => w.id === id);
  if (!wein) return <Leer text={t("Dieser Wein existiert nicht mehr.")} aktion={<Btn onClick={() => geheZu({ view: "weine" })}>{t("Zur Weinliste")}</Btn>} />;
  const winzer = winzerZuWein(db, wein);
  const region = regionZuWein(db, wein);
  /* Von der Schale, nicht aus db.prefs: In der Sicht „Alle Weine“ ist
     der Bestandsfilter gegenstandslos — Katalogobjekte haben nie
     Flaschen. Direkt gelesen blieben Listen dort leer, obwohl Weine
     vorhanden sind. */
  /* Titelbild: das Rundum-Etikett des jüngsten Jahrgangs, der eines
     hat, sonst das Etikett des jüngsten mit Foto — immer nur eines. */
  const rundumJg = titelRundum(wein);
  const titelJg = rundumJg ? null : titelFoto(wein);
  const jahrgaenge = [...wein.jahrgaenge].sort((a, b) => b.jahr - a.jahr);
  /* Anzeige respektiert „Nur mit Bestand"; die Vollliste bleibt für den
     Leerzustand erhalten. */
  const jahrgaengeSichtbar = nurBestand ? jahrgaenge.filter((jg) => gesamtFlaschen(jg) > 0) : jahrgaenge;
  const eigeneKritiken = [...kritikenFuer(db, wein.id)].sort((a, b) => b.jahr - a.jahr);
  /* Bewertungen anderer Konten zu diesem Wein. Sie stehen nicht
     ungefragt in der Tabelle: Wer seinen Keller ansieht, will zuerst
     die eigene Einschätzung sehen — die fremden sind ein Angebot,
     keine Ergänzung. Deshalb ein Schalter, und den nur, wenn es
     wirklich welche gibt. */
  const fremde = (fremdeKritiken || [])
    .filter((k) => k.weinId === wein.id && !eigeneKritiken.some((e) => e.id === k.id))
    .map((k) => ({ ...k, fremd: true }))
    .sort((a, b) => (b.jahr || 0) - (a.jahr || 0));
  /* Auch ohne eigene Kritik bleiben fremde zunächst verborgen: Der
     Blick auf den eigenen Wein soll zeigen, was man selbst festgehalten
     hat — und wenn das nichts ist, dann eben nichts. Das Angebot steht
     darunter. */
  const kritiken = zeigeFremde ? [...eigeneKritiken, ...fremde] : eigeneKritiken;

  return (
    <>
      <Seitenkopf
        krume={<><TextKnopf onClick={() => geheZu({ view: "weine" })}>{t("Weine")}</TextKnopf><span> / {wein.name}</span></>}
        titel={<>{wein.name}{wein.favorit && <span className="wl-favorit wl-favorit--titel" title={t("Lieblingswein")}>★</span>}</>}
        unterzeile={
          <>
            {winzer && <TextKnopf onClick={() => geheZu({ view: "winzerDetail", id: winzer.id })}>{winzer.name}</TextKnopf>}
            {region && <> · <TextKnopf onClick={() => geheZu({ view: "region", id: region.id })}>{region.name}</TextKnopf></>}
            {wein.appellation && <> · <TextKnopf className="ist-dezent" onClick={() => region && geheZu({ view: "region", id: region.id, appellation: wein.appellation })}>{wein.appellation}</TextKnopf></>}
            {wein.klassifikation && <> · {wein.klassifikation}</>}
            {" · "}<TypPunkt typ={wein.typ} />
            {rebsortenText(wein) && <> · {rebsortenText(wein)}</>}
            {wein.label && <> · <span className="wl-weinlabel">{wein.label}</span></>}
          </>
        }
        aktionen={
          <>
            <Btn onClick={() => oeffneModal({ typ: "wein", objekt: wein })}>{t("Bearbeiten")}</Btn>
            <LoeschenKnopf label={t("Wein löschen")} onDelete={() => { loescheWein(wein.id); geheZu({ view: "weine" }); }} />
          </>
        }
      />

      {(rundumJg || titelJg || wein.boden) && (
        <div className="wl-weinkopf">
          {rundumJg && (
            <RundumVorschau jg={rundumJg} className="wl-weinrundum"
              alt={`${t("Rundum-Etikett")} ${wein.name} ${jahrgangTitel(wein, rundumJg)}`} />
          )}
          {titelJg && (
            <button className="wl-weinfoto" onClick={() => setFotoGross(true)} title={t("Etikett vergrößern")} aria-label={t("Etikett vergrößern")}>
              <FotoBild pfad={(titelJg.foto || titelJg.katalogFoto).thumb || (titelJg.foto || titelJg.katalogFoto).pfad} alt={`Etikett ${wein.name}`} />
            </button>
          )}
          {wein.boden && (
            <p className="wl-bodenzeile"><span className="wl-feldname">{t("Boden")}</span>{wein.boden}</p>
          )}
        </div>
      )}

      {fotoGross && titelJg && (
        <div className="wl-fotolupe" onClick={() => setFotoGross(false)} role="dialog" aria-label={t("Etikett")}>
          <FotoBild pfad={(titelJg.foto || titelJg.katalogFoto).pfad} alt={`Etikett ${wein.name}`} className="ist-gross" />
        </div>
      )}

      <section className="wl-abschnitt">
        <div className="wl-abschnittskopf">
          <h2 className="wl-abschnittstitel">{t("Jahrgänge")}</h2>
          <Btn variante="voll" onClick={() => oeffneModal({ typ: "jahrgang", kontext: { weinId: wein.id } })}>{t("Jahrgang anlegen")}</Btn>
        </div>
        {jahrgaenge.length === 0 ? (
          <Leer text={t("Noch kein Jahrgang erfasst. Lege den ersten Jahrgang mit Analysewerten und Anfangsbestand an.")} />
        ) : jahrgaengeSichtbar.length === 0 ? (
          <Leer text={t("Kein Jahrgang dieses Weins hat aktuell Bestand. Deaktiviere „Nur mit Bestand“, um alle zu sehen.")} />
        ) : (
          <Begrenzt liste={jahrgaengeSichtbar} grenze={G.weinJahrgaenge} schluessel={`${wein.id}|${nurBestand}`}>{(zeilen) => (
          <div className="wl-scroll">
            <table className="wl-tabelle">
              <thead>
                <tr>
                  <th>{t("Jahrgang")}</th><th>{t("Alk.")}</th><th>{t("Säure")}</th><th>RZ</th><th>pH</th><th>{t("Geschmack")}</th>
                  <th>{t("Ausbau")}</th><th>{t("Preis")}</th><th>{t("Trinkfenster")}</th><th>{t("Lager")}</th><th>{t("Bestand")}</th><th className="wl-rechts"> </th>
                </tr>
              </thead>
              <tbody>
                {zeilen.map((jg) => (
                  <tr key={jg.id}>
                    <td><button className="wl-jahrnum wl-jahrlink num" onClick={() => geheZu({ view: "jahrgang", weinId: wein.id, jgId: jg.id })}>{jahrgangTitel(wein, jg)}</button></td>
                    <td className="num">{jg.alkohol != null ? `${fmtZahl(jg.alkohol, 1)} %` : "—"}</td>
                    <td className="num">{jg.saeure != null ? `${fmtZahl(jg.saeure, 1)} g/l` : "—"}</td>
                    <td className="num">{jg.restzucker != null ? `${fmtZahl(jg.restzucker, 1)} g/l` : "—"}</td>
                    <td className="num">{jg.ph != null ? fmtZahl(jg.ph, 2) : "—"}</td>
                    <td>{jg.zucker || "—"}</td>
                    <td className="wl-notiz">{jg.ausbau || "—"}</td>
                    <td className="num">{fmtEuro(jg.preis)}</td>
                    <td className="num">{trinkfensterText(jg)}</td>
                    <td>{jg.lagerort || "—"}</td>
                    <td><BestandAnzeige jahrgang={jg} /></td>
                    <td className="wl-rechts">
                      <ZeilenMenue beschriftung={`Aktionen für Jahrgang ${jahrLabel(jg.jahr)}`}
                        eintraege={jahrgangsMenue({ wein, jg, oeffneModal, loescheJahrgang, setzeFavorit })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}</Begrenzt>
        )}
      </section>

      <section className="wl-abschnitt">
        <div className="wl-abschnittskopf">
          <h2 className="wl-abschnittstitel">{t("Kritiken")}</h2>
          <Btn variante="voll" onClick={() => oeffneModal({ typ: "kritik", kontext: { weinId: wein.id } })}>{t("Kritik erfassen")}</Btn>
        </div>
        {kritiken.length === 0 ? (
          /* „Keine erfasst“ wäre falsch, wenn andere Konten längst
             etwas geschrieben haben — dann fehlt nur die eigene. */
          <Leer text={fremde.length > 0
            ? t("Du hast diesen Wein noch nicht bewertet.")
            : t("Noch keine Kritiken erfasst.")} />
        ) : (
          <Begrenzt liste={kritiken} grenze={G.kritiken} schluessel={`${wein.id}|${zeigeFremde}`}>{(zeilen) => (
          <div className="wl-scroll">
            <table className="wl-tabelle">
              <thead>
                <tr><th>{t("Jahrgang")}</th><th>{t("Kritiker / Quelle")}</th><th>{t("Bewertung")}</th><th>{t("Anlass / Pairing")}</th><th>{t("Notiz")}</th><th className="wl-rechts"> </th></tr>
              </thead>
              <tbody>
                {zeilen.map((k) => {
                  const kjg = jahrgangFuer(wein, k.jgId, k.jahr);
                  return (
                  <tr key={k.id}>
                    <td><span className="wl-jahrnum num">{kjg ? jahrgangTitel(wein, kjg) : jahrLabel(k.jahr)}</span></td>
                    <td>
                      {k.kritiker}
                      {/* Fremde Kritiken stammen aus dem gemeinsamen
                          Bestand. Wer sie geschrieben hat, gehört
                          dazu — sonst liest man sie als die eigene. */}
                      {k.fremd && <span className="wl-katalogmarke">{k.von || t("geteilt")}</span>}
                      <span className="wl-nebenzeile">{SCHEMATA[k.schema] ? SCHEMATA[k.schema].label : k.schema}</span>
                    </td>
                    <td>
                      <span className="wl-bewertung num">{wertFormat(k.schema, k.wert)}</span>
                      <KritikBalken anteil={normiert(k.schema, k.wert)} />
                    </td>
                    <td>
                      {(k.anlass || k.pairing)
                        ? <>{k.anlass && <span>{k.anlass}</span>}{k.anlass && k.pairing && <span className="wl-nebenzeile">{k.pairing}</span>}{!k.anlass && k.pairing && <span>{k.pairing}</span>}</>
                        : "—"}
                    </td>
                    <td className="wl-notiz">{k.text || "—"}</td>
                    <td className="wl-rechts">
                      {/* Fremde Kritiken gehören einem anderen Konto —
                          bearbeitbar sind sie nur dort. */}
                      {!k.fremd && (
                        <TextKnopf onClick={() => oeffneModal({ typ: "kritik", objekt: k, kontext: { weinId: wein.id } })}>{t("Bearbeiten")}</TextKnopf>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}</Begrenzt>
        )}
        {/* Nur anbieten, wenn es tatsächlich weitere gibt. */}
        {fremde.length > 0 && (
          <div className="wl-kritikmehr">
            <TextKnopf className="ist-dezent" onClick={() => setZeigeFremde((v) => !v)}>
              {zeigeFremde
                ? t("Nur eigene Kritiken zeigen")
                : t.plural(fremde.length,
                    "Alle Kritiken einblenden (eine weitere)",
                    "Alle Kritiken einblenden ({n} weitere)")}
            </TextKnopf>
          </div>
        )}
      </section>

      <BewegungsHistorie db={db} weinId={wein.id} oeffneModal={oeffneModal} />
    </>
  );
}


/* Kompakte Ein-/Auslagerungshistorie eines Weins. */
function BewegungsHistorie({ db, weinId, jgId = null, oeffneModal = null }) {
  let bewegungen = bewegungenVonWein(db, weinId);
  if (jgId) bewegungen = bewegungen.filter((b) => b.jgId === jgId);
  if (bewegungen.length === 0) return null;
  const fmtDatum = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  };
  return (
    <section className="wl-abschnitt">
      <h2 className="wl-abschnittstitel">{t("Ein- & Auslagerungen")}</h2>
      <Begrenzt liste={bewegungen} grenze={G.bewegungen} schluessel={`${weinId}|${jgId || ""}`}>{(zeilen) => (
      <ul className="wl-historie">
        {zeilen.map((b) => {
          const g = FLASCHENGROESSEN.find((x) => x.key === b.groesse);
          return (
            <li className="wl-histzeile" key={b.id}>
              <span className={"wl-histart wl-histart--" + b.art}>{b.art === "zugang" ? "Eingelagert" : b.art === "weinkarte" ? "Auf Weinkarte" : "Entnommen"}</span>
              <span className="wl-histmenge num">{b.art === "zugang" ? "+" : "−"}{b.menge} × {g ? g.kurz : b.groesse}</span>
              <span className="wl-histjahr num">Jg. {jahrLabel(b.jahr)}</span>
              <span className="wl-histquelle">
                {b.haendler || ""}
                {b.haendler && b.preis != null && " · "}
                {b.preis != null && <span className="num">{fmtEuro(b.preis)}/Fl.</span>}
              </span>
              <span className="wl-histdatum">{fmtDatum(b.datum)}</span>
              {oeffneModal && (
                <TextKnopf className="ist-dezent"
                  onClick={() => oeffneModal({ typ: "bewegung", objekt: b, kontext: { weinId: b.weinId, jgId: b.jgId, jahr: b.jahr } })}>
                  {t("Bearbeiten")}
                </TextKnopf>
              )}
            </li>
          );
        })}
      </ul>
      )}</Begrenzt>
    </section>
  );
}

export { BewegungsHistorie, WeinDetail };
